import {PaymoneyResultType} from '@web/core/constants'

type ResultTypeByFailedRetry = 'SUCCESS' | 'FAILED' | 'FAILED_RETRY_AVAILABLE'
type RechargeLimitType = 'BALANCE' | 'MONTH' | 'ONCE' | 'MINOR'

export type SubDetail = {
    /** 포인트 하위구분 이름 */
    text: string
    /** 포인트 하위구분의 잔액 */
    balance: number
}

export type RewardPointDescription = {
    /** 포인트 명 */
    text: string
    /** 포인트 잔액 */
    balance: number
    /** 로고 이미지의 URL */
    logoImageUrl?: string
    /** 포인트의 상세정보 */
    rewardPointDescriptionDetails?: SubDetail[]
}

/** 주문서 적립포인트 */
export type RewardPoint = {
    /** 적립포인트 조회 성공 여부 */
    success: boolean
    /** 적립포인트 잔액 */
    rewardPointBalance: Maybe<number>
    /** 현금성포인트 잔액 */
    cashableRewardPointBalance: Maybe<number>
    /** 적립포인트 상세 정보 */
    rewardPointDescriptions: RewardPointDescription[]
}

/** 충전포인트 잔액 */
export type ChargePointBalance = {
    /** 충전포인트 잔액 조회 성공 여부 타입 */
    resultType: Maybe<ResultTypeByFailedRetry>
    /** 충전포인트 잔액 */
    balance: Maybe<number>
    /** 충전포인트 사용가능 잔액 */
    availableBalance: Maybe<number>
    /** 충전포인트 회원 재조회 필요 여부 */
    shouldReloadChargePointUser: boolean
}

/** 충전 가능 충전포인트 */
export type RechargeableChargePoint = {
    /** 충전포인트 잔액 조회 성공 여부 타입 */
    resultType: Maybe<ResultTypeByFailedRetry>
    /** 충전포인트 충전 가능 금액 */
    reloadableRechargePointAmount: Maybe<number>
    /** 충전포인트 충전 제한 요인 */
    rechargeLimitType: Maybe<RechargeLimitType>
}

/** 적립포인트 잔액 재조회 응답 */
export type RewardPointBalanceReloadResponse = RewardPoint

/** 충전포인트 잔액 재조회 */
export type ChargePointBalanceReloadResponse = ChargePointBalance

/** 충전포인트 충전 가능 금액 재조회 응답 */
export type RechargeableChargePointReloadResponse = RechargeableChargePoint

/** 충전포인트와 적립포인트 잔액 재조회 응답 */
export type NaverpayPointBalanceReloadResponse = {
    /** 충전포인트 잔액 */
    chargePointBalance: ChargePointBalance
    /** 주문서 적립포인트 */
    rewardPoint: RewardPoint
}

export interface PointError {
    title: string
    status: number
    detail: string
    fallbackMessage: string
    code: string
    validationFailure: {
        message: string
        fallbackMessage: string
        reasonType: string
        reasonAttributes: object
    }
}

/**
 * 적립포인트 재조회 API 응답 모델 (25.04.16 부로 업데이트)
 */
export interface PaymoneyAndRewardResponse {
    paymoney: {
        resultType: PaymoneyResultType
        resultCode: string
        resultMessage: string
        coinType: string
        balanceAmount: number
        availableAmount: number
        overseasBalanceAmount: number
        overseasAvailableAmount: number
        cashableBalanceAmount: number
        includeNonCashEvidence: boolean
        renewYn: string
    }
    reward: RewardPointWithVoucherReloadResponse & {
        resultType: PaymoneyResultType
    }
}

/**
 * (혜택, 바우처포인트 포함) 적립포인트 재조회 API 파라미터
 */
export const RewardPointBalance = {
    /** 적립포인트 잔액 */
    EXCHANGE: 'EXCHANGE',
    /** 해외결제가능 포인트 잔액 */
    OVERSEAS: 'OVERSEAS',
    /** 선물가능 포인트잔액 */
    GIFT: 'GIFT',
    /** 바우처포인트 포함 */
    INCLUDE_VOUCHER: 'INCLUDE_VOUCHER',
    /** 총 바우처포인트 포함 */
    INCLUDE_TOTAL_VOUCHER: 'INCLUDE_TOTAL_VOUCHER',
} as const

type RewardPointBalanceType = Constant<typeof RewardPointBalance>

export const PayChannel = {
    C1: 'C1',
    C2: 'C2',
    DICON: 'DICON',
    BC: 'BC',
} as const

type PayChannelType = Constant<typeof PayChannel>

export const RewardPoint = {
    /** 적립예정 잔액 */
    PREPARE: 'PREPARE',
    /** 대기중 잔액 포함 */
    WAITING: 'WAITING',
    /** 복지 잔액 포함 */
    WELFARE: 'WELFARE',
} as const

type RewardPointType = Constant<typeof RewardPoint>

export type RewardPointWithVoucherReloadParams = {
    type: RewardPointBalanceType
    include: RewardPointType[]
    payChannel?: PayChannelType
    /** payChannel이 C1, C2인 경우 필수 */
    merchantNos?: string
    /** payChannel이 C2인 경우 필수 */
    middleCategoryIds?: string
    /** payChannel이 DICON인 경우 필수 */
    serviceSeq?: string
    /** payChannel이 BC인 경우 필수 */
    bcCode?: string
    bcMerchantNo?: string
}

export type RewardPointWithVoucherReloadResponse = {
    /** 성공일 경우 1000 응답 */
    resultCode: string
    /** 응답메시지 */
    resultMessage: string
    /** 총 잔액 */
    balanceAmount: number
    /** 대기중 포인트 잔액 > include=WAITING 인 경우 노출 */
    waitingBalanceAmount?: number
    /** 적립예정 포인트 잔액 > include=PREPARE 인 경우 노출 */
    prepareBalanceAmount?: number
    /** 페이포인트 복지포인트 잔액 > include=WELFARE 인 경우 노출 (cashableBalanceAmount) */
    welfareBalanceAmount?: number
    /** 포인트 잔액 상세정보 > 바우처 잔액이 있을 경우에만 필드 노출 */
    description: {
        /** 포인트 명 */
        text: string
        /** 포인트 잔액 */
        balance: number
        /** 로고 이미지의 URL */
        logoImageUrl?: string
        /** 포인트의 상세정보 */
        details?: SubDetail[]
    }[]
}
